/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {z} from '@hono/zod-openapi';

import {vehicleMappingConfigurationStatusSchema} from './VehicleMappingConfigurationStatusSchema';

export const vehicleMappingCoverageResultSchema = z.enum([
  'mapped',
  'taxonomy_missing',
  'ambiguous',
  'rule_invalid',
  'explicitly_unsupported',
  'evaluation_error',
]);

export const vehicleMappingCoverageCaseTypeSchema = z.enum([
  'MODEL_TAXONOMY',
  'FUEL_ENUM',
  'HYBRID_ENUM',
  'GOLDEN_SIGNATURE',
  'OBSERVED_SIGNATURE',
]);

export const vehicleMappingCoverageRunStatusSchema = z.enum([
  'queued',
  'running',
  'completed',
  'failed',
]);

export const vehicleMappingCoverageMetricSchema = z.object({
  total: z.number().int().nonnegative(),
  mapped: z.number().int().nonnegative(),
  taxonomyMissing: z.number().int().nonnegative(),
  ambiguous: z.number().int().nonnegative(),
  ruleInvalid: z.number().int().nonnegative(),
  explicitlyUnsupported: z.number().int().nonnegative(),
  evaluationError: z.number().int().nonnegative(),
  resolvedCoverage: z.number().min(0).max(1),
  mappingCoverage: z.number().min(0).max(1),
});

export const vehicleMappingCoverageSummarySchema = vehicleMappingCoverageMetricSchema.extend({
  failedGoldenExpectations: z.number().int().nonnegative(),
  byCaseType: z.record(vehicleMappingCoverageCaseTypeSchema, vehicleMappingCoverageMetricSchema),
});

export const vehicleMappingCoverageComparisonSchema = z.object({
  comparedRunId: z.string().uuid(),
  kind: z.enum(['taxonomy_regression', 'rule_regression', 'dataset_delta', 'mixed_change']),
  byCaseType: z.record(
    vehicleMappingCoverageCaseTypeSchema,
    z.object({
      total: z.number().int(),
      mapped: z.number().int(),
      taxonomyMissing: z.number().int(),
      ambiguous: z.number().int(),
      ruleInvalid: z.number().int(),
      explicitlyUnsupported: z.number().int(),
      evaluationError: z.number().int(),
      resolvedCoverage: z.number().min(-1).max(1),
      mappingCoverage: z.number().min(-1).max(1),
    })
  ),
});

export const vehicleMappingCoverageRunSchema = z.object({
  id: z.string().uuid(),
  platformCode: z.string(),
  configurationBundleId: z.string().uuid(),
  taxonomyBatchId: z.string().uuid(),
  alphaSnapshotId: z.string().uuid(),
  alphaContentHash: z.string(),
  alphaSchemaVersion: z.number().int().positive(),
  alphaRecordCount: z.number().int().nonnegative(),
  mappingRuleSetId: z.string().uuid(),
  configurationStatus: vehicleMappingConfigurationStatusSchema,
  canActivate: z.boolean(),
  status: vehicleMappingCoverageRunStatusSchema,
  totalCount: z.number().int().nonnegative(),
  processedCount: z.number().int().nonnegative(),
  resultSummary: vehicleMappingCoverageSummarySchema.nullable(),
  failureReason: z.string().nullable(),
  detailsExpireAt: z.string().datetime(),
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  comparison: vehicleMappingCoverageComparisonSchema.nullable().optional(),
});

export const vehicleMappingCoverageIssueSchema = z.object({
  id: z.string().uuid(),
  coverageRunId: z.string().uuid(),
  signatureHash: z.string(),
  vehicleType: z.string(),
  make: z.string().nullable(),
  modelFamily: z.string().nullable(),
  fuelType: z.string().nullable(),
  hybridType: z.string().nullable(),
  bodyStyle: z.string().nullable(),
  transmission: z.string().nullable(),
  caseType: vehicleMappingCoverageCaseTypeSchema,
  result: vehicleMappingCoverageResultSchema,
  reasonCode: z.string().nullable(),
  blockingDimension: z.string().nullable(),
  candidateExternalIds: z.array(z.string()),
  selectedExternalIds: z.array(z.string()),
  executionTrace: z.array(z.record(z.string(), z.unknown())),
  goldenExpectationId: z.string().uuid().nullable(),
  goldenExpectationRevisionId: z.string().uuid().nullable(),
  remediationReason: z.string().nullable(),
  remediationStatus: z.enum(['pending', 'validation_queued']).nullable(),
  createdAt: z.string().datetime(),
});

export const vehicleMappingCoverageIssuesResponseSchema = z
  .object({
    items: z.array(vehicleMappingCoverageIssueSchema),
    pagination: z.object({
      limit: z.number().int().positive(),
      offset: z.number().int().nonnegative(),
      total: z.number().int().nonnegative(),
    }),
    counts: z.object({
      all: z.number().int().nonnegative(),
      actionable: z.number().int().nonnegative(),
      mapped: z.number().int().nonnegative(),
      taxonomyMissing: z.number().int().nonnegative(),
      ambiguous: z.number().int().nonnegative(),
      ruleInvalid: z.number().int().nonnegative(),
      explicitlyUnsupported: z.number().int().nonnegative(),
      evaluationError: z.number().int().nonnegative(),
    }),
  })
  .openapi('VehicleMappingCoverageIssuesResponse');

export const vehicleMappingCoverageListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(250).default(50),
  offset: z.coerce.number().int().nonnegative().default(0),
});

export const vehicleMappingCoverageIssuesQuerySchema = vehicleMappingCoverageListQuerySchema.extend(
  {
    result: vehicleMappingCoverageResultSchema.optional(),
    scope: z.enum(['all', 'actionable']).optional(),
    search: z.string().trim().max(100).optional(),
    caseType: vehicleMappingCoverageCaseTypeSchema.optional(),
    vehicleType: z.string().optional(),
    make: z.string().optional(),
    modelFamily: z.string().optional(),
    fuelType: z.string().optional(),
    hybridType: z.string().optional(),
  }
);

export const enqueueVehicleMappingCoverageResponseSchema = z.object({
  runId: z.string().uuid(),
});

const vehicleMappingDimensionSchema = z.enum(['make', 'model', 'fuel', 'hybrid']);

export const vehicleMappingCoverageCandidateSchema = z.object({
  dimension: vehicleMappingDimensionSchema,
  externalId: z.string(),
  label: z.string(),
  makeExternalId: z.string().nullable(),
  makeLabel: z.string().nullable(),
});

export const vehicleMappingCoverageIssueDetailSchema = vehicleMappingCoverageIssueSchema.extend({
  configurationBundleId: z.string().uuid(),
  taxonomyBatchId: z.string().uuid(),
  mappingRuleSetId: z.string().uuid(),
  candidates: z.array(vehicleMappingCoverageCandidateSchema),
});

const vehicleMappingCoverageRemediationBaseSchema = z.object({
  reason: z.string().trim().max(1000).default(''),
});

export const vehicleMappingSourceSignatureSchema = z.object({
  vehicleType: z.string().trim().min(1).max(100),
  make: z.string().trim().min(1).max(100),
  modelFamily: z.string().trim().min(1).max(100),
  fuelType: z.string().trim().min(1).max(100).nullable().default(null),
  hybridType: z.string().trim().min(1).max(100).nullable().default(null),
  bodyStyle: z.string().trim().min(1).max(100).nullable().default(null),
  transmission: z.string().trim().min(1).max(100).nullable().default(null),
});

export const vehicleMappingRuleRemediationRequestSchema =
  vehicleMappingCoverageRemediationBaseSchema.extend({
    kind: z.literal('mapping_rule'),
    targetDimension: vehicleMappingDimensionSchema,
    targetExternalId: z.string().min(1),
  });

export const vehicleMappingManualRuleRequestSchema = z.object({
  source: vehicleMappingSourceSignatureSchema,
  request: vehicleMappingRuleRemediationRequestSchema,
});

const vehicleMappingUnsupportedRemediationRequestSchema =
  vehicleMappingCoverageRemediationBaseSchema.extend({
    kind: z.literal('unsupported'),
    scope: vehicleMappingDimensionSchema,
  });

const vehicleMappingRuleDisableRequestSchema = vehicleMappingCoverageRemediationBaseSchema.extend({
  kind: z.literal('disable_rule'),
  ruleId: z.string().uuid(),
});

export const vehicleMappingRuleDisableBodySchema = vehicleMappingCoverageRemediationBaseSchema;

export const vehicleMappingCoverageRemediationRequestSchema = z.discriminatedUnion('kind', [
  vehicleMappingRuleRemediationRequestSchema,
  vehicleMappingUnsupportedRemediationRequestSchema,
]);

export const vehicleMappingStoredRemediationRequestSchema = z.discriminatedUnion('kind', [
  vehicleMappingRuleRemediationRequestSchema,
  vehicleMappingUnsupportedRemediationRequestSchema,
  vehicleMappingRuleDisableRequestSchema,
]);

export const vehicleMappingCoverageRemediationPreviewSchema = z.object({
  kind: z.enum(['mapping_rule', 'unsupported']),
  affectedCaseCount: z.number().int().nonnegative(),
  examples: z.array(
    z.object({
      caseType: vehicleMappingCoverageCaseTypeSchema,
      vehicleType: z.string(),
      make: z.string().nullable(),
      modelFamily: z.string().nullable(),
      fuelType: z.string().nullable(),
      hybridType: z.string().nullable(),
      result: vehicleMappingCoverageResultSchema,
    })
  ),
  target: vehicleMappingCoverageCandidateSchema.nullable(),
  nextRuleSetRevision: z.number().int().positive(),
});

const vehicleMappingResolutionSchema = z.object({
  result: vehicleMappingCoverageResultSchema,
  reasonCode: z.string().nullable(),
  selectedExternalIds: z.array(z.string()),
  appliedRuleId: z.string().nullable(),
});

export const vehicleMappingManualRemediationPreviewSchema =
  vehicleMappingCoverageRemediationPreviewSchema.extend({
    source: vehicleMappingSourceSignatureSchema,
    sourcePresentInSnapshot: z.boolean(),
    before: vehicleMappingResolutionSchema,
    after: vehicleMappingResolutionSchema,
  });

export const vehicleMappingCoverageRemediationResponseSchema = z.object({
  remediationId: z.string().uuid(),
  pendingCount: z.number().int().positive(),
});

export const vehicleMappingStoredSourceSignatureSchema = z.object({
  vehicleType: z.string(),
  make: z.string().nullable(),
  modelFamily: z.string().nullable(),
  fuelType: z.string().nullable(),
  hybridType: z.string().nullable(),
  bodyStyle: z.string().nullable(),
  transmission: z.string().nullable(),
});

export const vehicleMappingCoverageRemediationBatchSchema = z.object({
  sourceCoverageRunId: z.string().uuid(),
  pendingCount: z.number().int().nonnegative(),
  remediations: z.array(
    z.object({
      id: z.string().uuid(),
      sourceCoverageIssueId: z.string().uuid().nullable(),
      source: vehicleMappingStoredSourceSignatureSchema,
      request: vehicleMappingStoredRemediationRequestSchema,
      reason: z.string(),
      createdBy: z.string(),
      createdAt: z.string().datetime(),
    })
  ),
});

const vehicleMappingRuleSchema = z.object({
  id: z.string().uuid(),
  revisionId: z.string().uuid(),
  revision: z.number().int().positive(),
  source: vehicleMappingStoredSourceSignatureSchema,
  targetDimension: vehicleMappingDimensionSchema,
  targetExternalId: z.string(),
  target: vehicleMappingCoverageCandidateSchema.nullable(),
  reason: z.string(),
  createdBy: z.string(),
  createdAt: z.string().datetime(),
});

export const vehicleMappingRulesResponseSchema = z.object({
  mappingRuleSetId: z.string().uuid(),
  rules: z.array(vehicleMappingRuleSchema),
});

export const vehicleMappingManualRuleOptionsQuerySchema = z.object({
  make: z.string().trim().min(1).max(100).optional(),
});

export const vehicleMappingManualRuleOptionsSchema = z.object({
  supportedVehicleTypes: z.array(z.string()),
  targets: z.array(vehicleMappingCoverageCandidateSchema),
});

export const vehicleMappingCoverageRemediationValidationResponseSchema = z.object({
  remediationCount: z.number().int().positive(),
  mappingRuleSetId: z.string().uuid(),
  configurationBundleId: z.string().uuid(),
  coverageRunId: z.string().uuid(),
});
